<script lang="ts" setup>
	import {useSidebar} from '@/components/ui/sidebar';
	import {useHeader} from '@/scripts/composables/use-header.ts';

	const {isAvailable} = useSidebar();
	const {breadcrumbs} = useHeader();

	const MAX_BREADCRUMBS_WITHOUT_DROPDOWN = 3;

	const tooManyBreadcrumbs = computed(
		() =>
			(breadcrumbs.value?.length ?? 0) > MAX_BREADCRUMBS_WITHOUT_DROPDOWN
	);

	const firstBreadcrumb = computed(() => {
		if (!breadcrumbs.value?.length) return {};

		return breadcrumbs.value[0];
	});

	const dropdownBreadcrumbs = computed(() => {
		if (!tooManyBreadcrumbs.value) return [];

		return breadcrumbs.value.slice(
			1,
			breadcrumbs.value.length - MAX_BREADCRUMBS_WITHOUT_DROPDOWN + 1
		);
	});

	const normalBreadcrumbs = computed(() => {
		if (!tooManyBreadcrumbs.value) return breadcrumbs.value;

		return (
			breadcrumbs.value?.slice(
				breadcrumbs.value.length - MAX_BREADCRUMBS_WITHOUT_DROPDOWN + 1
			) ?? []
		);
	});
</script>

<template>
	<header
		class="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12"
	>
		<div class="flex items-center gap-2 px-4">
			<SidebarTrigger
				v-if="isAvailable"
				class="-ml-1"
			/>
			<Separator
				v-if="isAvailable"
				class="mr-2 h-4"
				orientation="vertical"
			/>
			<Breadcrumb>
				<BreadcrumbList>
					<template v-if="tooManyBreadcrumbs">
						<BreadcrumbItem class="hidden cursor-pointer md:block">
							<BreadcrumbLink as-child>
								<RouterLink
									:href="firstBreadcrumb.url"
									@click="
										'command' in firstBreadcrumb
											? firstBreadcrumb.command()
											: undefined
									"
								>
									<component :is="firstBreadcrumb.icon" />
									{{ firstBreadcrumb.label }}
								</RouterLink>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator class="hidden md:block" />
						<BreadcrumbItem class="hidden cursor-pointer md:block">
							<DropdownMenu>
								<DropdownMenuTrigger
									class="flex items-center gap-1"
								>
									<BreadcrumbEllipsis class="h-4 w-4" />
									<span class="sr-only"
										>{{ $t('ui.menu.toggle') }}
									</span>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start">
									<DropdownMenuItem
										v-for="breadcrumb in dropdownBreadcrumbs"
										:key="breadcrumb.key"
										as-child
									>
										<RouterLink
											:href="breadcrumb.url"
											@click="
												'command' in breadcrumb
													? breadcrumb.command()
													: undefined
											"
										>
											<component :is="breadcrumb.icon" />
											{{ breadcrumb.label }}
										</RouterLink>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</BreadcrumbItem>
						<BreadcrumbSeparator class="hidden md:block" />
					</template>
					<template
						v-for="(breadcrumb, index) in normalBreadcrumbs"
						:key="breadcrumb.key"
					>
						<template v-if="index !== normalBreadcrumbs.length - 1">
							<BreadcrumbItem
								class="hidden cursor-pointer md:block"
							>
								<BreadcrumbLink as-child>
									<RouterLink
										:href="breadcrumb.url"
										@click="
											'command' in breadcrumb
												? breadcrumb.command()
												: undefined
										"
									>
										<component :is="breadcrumb.icon" />
										{{ breadcrumb.label }}
									</RouterLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator class="hidden md:block" />
						</template>
						<BreadcrumbItem v-else>
							<BreadcrumbPage>
								{{ breadcrumb.label }}
							</BreadcrumbPage>
						</BreadcrumbItem>
					</template>
				</BreadcrumbList>
			</Breadcrumb>
		</div>
	</header>
</template>
